const CHALLENGE_LIFETIME_MS = 10 * 60 * 1000;
const REQUEST_LIMIT = 60;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const INVALID_CODE = 'That code is invalid or expired. Request a new code if needed.';

/** Browser-only recovery for opaque resend responses. Nothing persists to storage. */
export class RecruitVerificationRequests {
  private challenges: { id: string; seenAt: number }[] = [];
  private requests: number[] = [];

  constructor(initialChallenge?: string) {
    if (initialChallenge) {
      this.remember(initialChallenge);
    }
  }

  remember(id: string) {
    const now = Date.now();
    this.challenges = this.challenges.filter(item => now - item.seenAt < CHALLENGE_LIFETIME_MS);
    if (!this.challenges.some(item => item.id === id)) {
      this.challenges.push({ id, seenAt: now });
    }
  }

  clear() {
    this.challenges = [];
    // Changing NMLS must not reset the local request budget.
  }

  async run(endpoint: string, action: 'start' | 'verify', nmls: string, code: string, signal: AbortSignal): Promise<string | undefined> {
    const now = Date.now();
    this.challenges = this.challenges.filter(item => now - item.seenAt < CHALLENGE_LIFETIME_MS);
    const ids = action === 'start' ? [''] : this.challenges.map(item => item.id).reverse();
    if (!ids.length) {
      throw new Error(INVALID_CODE);
    }
    for (const id of ids) {
      signal.throwIfAborted();
      this.requests = this.requests.filter(at => Date.now() - at < CHALLENGE_LIFETIME_MS);
      if (this.requests.length >= REQUEST_LIMIT) {
        throw new Error('Too many verification attempts. Please wait and try again.');
      }
      this.requests.push(Date.now());
      let response: Response;
      let result: { error?: unknown; challenge_id?: unknown };
      try {
        response = await fetch(endpoint, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' }, signal,
          body: JSON.stringify({ action, nmls, challenge_id: id, code })
        });
        result = await response.json();
        if (!result || typeof result !== 'object') {
          throw new Error('Invalid response');
        }
      } catch {
        signal.throwIfAborted();
        throw new Error('Unable to complete verification. Please try again.');
      }
      signal.throwIfAborted();
      if (action === 'verify' && response.status === 401 && result.error === 'invalid_code') {
        continue;
      }
      if (!response.ok) {
        throw new Error(response.status === 429
          ? 'Too many verification attempts. Please wait and try again.'
          : 'Unable to complete verification. Please try again.');
      }
      if (action === 'start') {
        if (typeof result.challenge_id !== 'string' || !UUID.test(result.challenge_id)) {
          throw new Error('Unable to complete verification. Please try again.');
        }
        this.remember(result.challenge_id);
        return result.challenge_id;
      }
      this.clear();
      return;
    }
    throw new Error(INVALID_CODE);
  }
}
